import { z } from "zod";

// Tipos e helpers compartilhados do catálogo de comandos. Extraído de
// assistant-catalog.ts (guardrail max-lines 200) para que as famílias (display/simulation/
// interpretation/…) importem daqui sem ciclo com o barrel que as compõe.

// Bump: `nav.organization` entrou no catálogo (tela do expediente da fábrica). Comando
// novo muda a superfície que o modelo enxerga; os campos aditivos de PROJEÇÃO da mesma spec
// (`calendarSource`) não entram nesta conta — só `argsJsonSchema` e o conjunto de comandos contam.
// Bump anterior: o enum de objetivos ganhou OP_ON_TIME e `simulation.suggestImprovements`
// mudou a obrigatoriedade de `targetQuantity` (agora exigida só por TIME_TO_QUANTITY).
// `dueDate` entrou nos args de productionOrder.create/update.
// `nav.appliedScenarios` entrou no catálogo (comando NOVO ⇒ conta). As tools de leitura
// do compromisso e da apuração (`applied_scenarios_list`, `applied_scenario_read`) NÃO contam:
// tool MCP não é comando, e a saída delas é aditiva.
// A projeção do compromisso ganhou aderência, elegibilidade e gatilho de apuração.
// SEM bump: é saída ADITIVA de tool de leitura, e nenhum `argsJsonSchema` de comando mudou — a
// mesma regra já aplicada antes. Bumpar por saída aditiva faria a versão perder o
// significado que ela tem: "o contrato de COMANDOS mudou, revalide o que o agente pode disparar".
// Spec 078 — bump: `parameter.create`/`parameter.update` ganharam `visualRole` (papel na cena 3D)
// e `limitMax`/`limitMin` (alarme por limite, com a faixa da coluna `Decimal(12,3)` no schema) —
// é mudança de `argsJsonSchema`, logo conta. Os três
// `nav.*` da cena (lineScene, modelsLibrary — sem dígito: invariante de nome do catálogo —, lineAmbient) entram no mesmo bump (fase de polish da 078).
// Spec 078 (revisão pós-code-review) — bump: `limitMax`/`limitMin` ganharam faixa no
// `argsJsonSchema` (o `Decimal(12,3)` da coluna, que antes só estourava no banco como 500). É
// mudança do que o modelo enxerga, então conta — mesmo sendo aperto e não campo novo.
//
// Merge 077 × 078 — bump obrigatório: as duas specs subiram a versão em paralelo (`2026-09-02a`
// na 077, `2026-09-03b` na 078) e o catálogo integrado contém AS DUAS mudanças. Manter qualquer
// um dos dois valores faria uma engine em cache acreditar conhecer um catálogo que não existiu:
// nem a 077 sozinha, nem a 078 sozinha.
//
// Spec 080 — bump obrigatório: `parameter.create` deriva seus args de `CreateParameterRequestSchema`
// e o membro BOOLEAN ganhou `isRestriction` e `visualRole`. Não é alargamento de superfície — o
// agente já podia gravar os dois campos por `parameter.update` em qualquer categoria, e por
// `parameter.create` em NUMERIC; mantê-los fora só do create booleano deixaria o agente capaz de
// criar e não de configurar o relé térmico. Quem pegou foi o snapshot do manifesto, como desenhado.
// Spec 082 — bump obrigatório: `production.registerLoss` deriva seus args de
// `ProductionLossSchema`, que ganhou `productId`. É ALARGAMENTO de superfície, não campo aditivo
// em projeção: o agente passa a poder dizer de qual produto é a perda, e uma engine em cache que
// não soubesse do campo continuaria lançando perda sem produto para sempre. Quem pegou foi o
// snapshot do manifesto, como desenhado.
// Spec 084 — bump obrigatório: `productionOrder.create` e `productionOrder.update` ganharam
// `characteristics` (a marca da batelada no agendamento) — alargamento de `argsJsonSchema`, logo
// conta. E `nav.lineBatches` é entrada NOVA no catálogo. Os args novos de `nav.productionReport`
// (recorte e quadro) e de `nav.organization` (`section`) entram no mesmo bump. As duas tools de
// leitura da batelada NÃO contam sozinhas — tool MCP não é comando —, mas vêm na mesma fatia.
export const CATALOG_VERSION = "2026-09-16a";

export type CommandKind = "display" | "action" | "navigate";
export type CommandRiskTier = "safe" | "confirm" | "destructive";
export type CommandRenderTarget = "inline" | "panel";

export interface CommandSpec {
  name: string;
  kind: CommandKind;
  riskTier: CommandRiskTier;
  minRole: "OPERADOR" | "GESTOR"; // OPERADOR = ambos os papéis podem
  argsSchema: z.ZodTypeAny; // sempre .strict(): campo extra ⇒ comando inválido ⇒ descartado
  renderTarget?: CommandRenderTarget; // só display
  description: string; // PT-BR; vai no manifesto (o modelo lê)
}

// Mapas para os enums do Prisma (o worker persiste em UPPERCASE).
export const COMMAND_KIND_TO_PRISMA = {
  display: "DISPLAY",
  action: "ACTION",
  navigate: "NAVIGATE",
} as const;
export const RISK_TIER_TO_PRISMA = {
  safe: "SAFE",
  confirm: "CONFIRM",
  destructive: "DESTRUCTIVE",
} as const;

// Helpers de argumento reusados pelas famílias.
export const Uuid = z.string().uuid();
export const IsoDate = z.string().datetime();

// Refine p/ comandos de update parcial: exige ao menos um campo além do(s) id — senão o comando é um
// no-op inútil. Reusado pelas famílias que fazem `*.update` (estrutura, params, dispositivos).
export const hasUpdate =
  (idKeys: readonly string[]) =>
  (v: Record<string, unknown>): boolean =>
    Object.keys(v).some((k) => !idKeys.includes(k) && v[k] !== undefined);
export const UPDATE_MSG = { message: "Informe ao menos um campo para atualizar." };

/**
 * Spec 084 — a MARCA da batelada nos comandos de ordem (`create` e `update`).
 *
 * Mora aqui, e não duplicada nos dois, porque os dois precisam da MESMA regra: valor da lista XOR
 * texto livre. Duas cópias divergiriam na primeira mudança, e a que ficasse para trás aceitaria um
 * argumento que o service recusa — o agente proporia um comando que morre na confirmação.
 *
 * Os ids vêm da tool `batchCharacteristics_read`. O service cobra as obrigatórias da linha e recusa
 * valor de característica que não é marcada lá; o `.strict()` daqui só impede chave inventada.
 */
export const BatchCharacteristicArgs = z
  .array(
    z
      .object({
        characteristicId: Uuid,
        valueId: Uuid.optional(),
        text: z.string().trim().min(1).max(200).optional(),
      })
      .strict()
      .refine((v) => (v.valueId === undefined) !== (v.text === undefined), {
        message: "Informe um valor da lista ou um texto, não os dois.",
      })
  )
  .max(50)
  .optional();
